import type { TFunction } from "i18next";
import { VoteParser } from "./VoteParser";

/** Уровень разброса для UI */
export enum SpreadLevel {
    High = "high",
    Medium = "medium",
    Low = "low",
    NoData = "noData",
}

/** Статистика голосов */
export type Stats = {
    average?: number;
    median?: number | string;
    range?: number;
    recommended?: number | string;
    spread?: SpreadLevel;
};

// Служебные значения, исключаемые из расчёта
const EXCLUDED = new Set(["?"]);

/** Компаратор значений с учётом шкалы */
function makeComparator(scale?: (number | string)[]) {
    const idx: Record<string, number> = {};
    scale?.forEach((v, i) => (idx[String(v)] = i));
    return (a: number | string, b: number | string) => {
        const ai = idx[String(a)];
        const bi = idx[String(b)];
        const aIn = ai !== undefined;
        const bIn = bi !== undefined;

        if (aIn && bIn) return ai - bi;
        if (aIn && !bIn) return -1;
        if (!aIn && bIn) return 1;

        const an = typeof a === "number" ? a : Number(a);
        const bn = typeof b === "number" ? b : Number(b);
        const aNum = !Number.isNaN(an);
        const bNum = !Number.isNaN(bn);
        if (aNum && bNum) return an - bn;

        return String(a).localeCompare(String(b));
    };
}

/** Преобразуем к числу, если возможно */
function toNumberMaybe(v: any): number | null {
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v))) return Number(v);
    return null;
}

/** Фильтруем null/undefined и служебные значения */
function normalizeVotes(values: (number | string | null | undefined)[]): (number | string)[] {
    return values
        .filter((v): v is number | string => v != null)
        .filter((v) => !EXCLUDED.has(String(v).trim()));
}

/** Медиана (чётный → верхний сосед; с учётом шкалы). Возвращает "-" при отсутствии данных. */
export function calculateMedian(
    values: (number | string)[],
    scale?: (number | string)[]
): number | string | "-" {
    const cleaned = normalizeVotes(values);
    if (cleaned.length === 0) return "-";
    const sorted = [...cleaned].sort(makeComparator(scale));
    const n = sorted.length;
    const midUpper = Math.ceil((n - 1) / 2); // чётный → верхний
    return sorted[midUpper];
}

/** Диапазон по числам */
function calculateRange(numbers: number[]): number | null {
    if (numbers.length === 0) return null;
    return Math.max(...numbers) - Math.min(...numbers);
}


/** Универсальный текст-объяснение */
export function explainSpreadAuto(
    spread: SpreadLevel | undefined,
    range: number | null | undefined,
    t: TFunction
): string {
    if (!spread || spread === SpreadLevel.NoData) return t("ai.noData");

    switch (spread) {
        case SpreadLevel.High:
            return t("ai.highSpread", { value: VoteParser.formatMaybeNumber(range) });
        case SpreadLevel.Medium:
            return t("ai.mediumSpread", { value: VoteParser.formatMaybeNumber(range) });
        case SpreadLevel.Low:
            return t("ai.lowSpread", { value: VoteParser.formatMaybeNumber(range) });
        default:
            return t("ai.noData");
    }
}

/** Индексы по текущей шкале; значения, отсутствующие в шкале, исключаем */
function mapVotesToIndices(
    values: (number | string)[],
    scale: (number | string)[]
): number[] {
    const index = new Map<string, number>();
    scale.forEach((v, i) => index.set(String(v), i));
    return values
        .map((v) => index.get(String(v)))
        .filter((i): i is number => typeof i === "number");
}

/** Частоты по индексам */
function freq(indices: number[]): Map<number, number> {
    const m = new Map<number, number>();
    for (const i of indices) m.set(i, (m.get(i) ?? 0) + 1);
    return m;
}

/** Находим «уникальное простое большинство»: возвращаем индекс, если есть ровно один кандидат с count ≥ ceil(n/2) */
function uniqueSimpleMajority(counts: Map<number, number>, n: number): number | undefined {
    const threshold = Math.ceil(n / 2);
    const candidates = Array.from(counts.entries()).filter(([, c]) => c >= threshold);
    if (candidates.length !== 1) return undefined;
    return candidates[0][0];
}

/** Медиана по индексам: при чётном n — среднее двух центральных индексов (может быть дробным) */
function medianIndex(sortedIdx: number[]): number {
    const n = sortedIdx.length;
    const mid = Math.floor(n / 2);
    if (n % 2 === 1) return sortedIdx[mid];
    return (sortedIdx[mid - 1] + sortedIdx[mid]) / 2;
}

/** Округляем к ближайшему индексу с tie-break:
 *  1) выше частота по голосам на индексе
 *  2) выше «локальная плотность»: count(i-1)+count(i)+count(i+1)
 *  3) верхний индекс (детерминизм)
 */
function roundToNearestIndex(
    x: number,
    counts: Map<number, number>,
    minIndex: number,
    maxIndex: number
): number {
    // базовая ближайшая пара
    let i0 = Math.floor(x);
    let i1 = Math.ceil(x);
    if (i0 < minIndex) i0 = minIndex;
    if (i1 > maxIndex) i1 = maxIndex;

    // если нет «между», просто клип/округление
    if (i0 === i1) return i0;

    // расстояния одинаковые → tie-break
    const c0 = counts.get(i0) ?? 0;
    const c1 = counts.get(i1) ?? 0;
    if (c0 !== c1) return c0 > c1 ? i0 : i1;

    const d0 = (counts.get(i0 - 1) ?? 0) + c0 + (counts.get(i0 + 1) ?? 0);
    const d1 = (counts.get(i1 - 1) ?? 0) + c1 + (counts.get(i1 + 1) ?? 0);
    if (d0 !== d1) return d0 > d1 ? i0 : i1;

    // детерминированный fallback — вверх
    return Math.max(i0, i1);
}

/** Классификация разброса */
function classifySpread(
    values: (number | string | null | undefined)[],
    scale?: (number | string)[]
): SpreadLevel {
    const cleaned = normalizeVotes(values);
    if (cleaned.length === 0) return SpreadLevel.NoData;

    // Если есть числа → по числовому диапазону
    const nums = cleaned.map(toNumberMaybe).filter((x): x is number => x != null);
    if (nums.length > 0) {
        const rng = calculateRange(nums);
        if (rng == null) return SpreadLevel.NoData;
        if (rng > 10) return SpreadLevel.High;
        if (rng > 3) return SpreadLevel.Medium;
        return SpreadLevel.Low;
    }

    // Иначе порядковая шкала
    if (scale?.length) {
        const idxs = mapVotesToIndices(cleaned, scale);
        if (idxs.length === 0) return SpreadLevel.NoData;
        const span = Math.max(...idxs) - Math.min(...idxs);
        if (span >= 4) return SpreadLevel.High;
        if (span >= 2) return SpreadLevel.Medium;
        return SpreadLevel.Low;
    }

    return SpreadLevel.NoData;
}

/** Рекомендация (v2) — работает в ИНДЕКСАХ шкалы */
export function calculateRecommended(
    values: (number | string | null | undefined)[],
    scale?: (number | string)[]
): number | string | undefined {
    // фильтруем служебные/пустые
    const cleanedRaw = normalizeVotes(values);
    if (cleanedRaw.length === 0) return undefined;

    // n < 2 → 1 голос → он и есть рекомендация;
    if (cleanedRaw.length === 1) return cleanedRaw[0];

    // Без шкалы корректно реализовать v2 нельзя — fallback: медиана
    if (!scale || scale.length === 0) {
        const med = calculateMedian(cleanedRaw);
        return med === "-" ? undefined : med;
    }

    // Исключаем значения, которых нет в ТЕКУЩЕЙ шкале (шкалу могли сменить после голосования)
    const idx = mapVotesToIndices(cleanedRaw, scale).sort((a, b) => a - b);
    const n = idx.length;
    if (n === 0) return undefined;
    if (n === 1) return scale[idx[0]];

    const minIdx = idx[0];
    const maxIdx = idx[n - 1];
    const spreadSteps = maxIdx - minIdx;

    const counts = freq(idx);

    // === Правило 1: простое большинство (порог >= ceil(n/2)) — только если ОДИН кандидат удовлетворяет
    const majorityIndex = uniqueSimpleMajority(counts, n);
    if (majorityIndex !== undefined) {
        return scale[Math.max(0, Math.min(majorityIndex, scale.length - 1))];
    }

    // === Правило 2: низкий разброс (≤1) — ближайшее к СРЕДНЕМУ
    if (spreadSteps <= 1) {
        const meanIdx = idx.reduce((a, b) => a + b, 0) / n;
        const rec = roundToNearestIndex(meanIdx, counts, 0, scale.length - 1);
        return scale[rec];
    }

    // === Правило 3: средний разброс (2..3) — ближайшее к МЕДИАНЕ
    if (spreadSteps <= 3) {
        const medIdx = medianIndex(idx); // может быть дробным
        const rec = roundToNearestIndex(medIdx, counts, 0, scale.length - 1);
        return scale[rec];
    }

    // === Правило 4: высокий разброс (≥4) — обрезанное среднее (trim 1–1 при n≥4), иначе обычное среднее
    let trimmed = idx;
    if (n >= 4) trimmed = idx.slice(1, n - 1);
    const meanTrim = trimmed.reduce((a, b) => a + b, 0) / trimmed.length;
    const rec = roundToNearestIndex(meanTrim, counts, 0, scale.length - 1);
    return scale[rec];
}

/** Публичный фасад */
export class VoteStats {
    static calculateMedian = calculateMedian;
    static calculateRecommended = calculateRecommended;

    static classifySpread(
        values: (number | string | null | undefined)[],
        scale?: (number | string)[]
    ): SpreadLevel {
        return classifySpread(values, scale);
    }

    static calcStatsFromVotes(
        votes: Record<string, number | string | null | undefined>,
        scale?: (number | string)[]
    ): Stats {
        const all = Object.values(votes);

        const numeric = all
            .map(toNumberMaybe)
            .filter((x): x is number => x != null);

        const average =
            numeric.length > 0
                ? numeric.reduce((a, b) => a + b, 0) / numeric.length
                : undefined;

        const median = calculateMedian(
            all.filter((v): v is number | string => v != null),
            scale
        );

        const range = calculateRange(numeric) ?? undefined;

        const recommended = calculateRecommended(all, scale);

        const spread = classifySpread(all, scale);

        return { average, median, range, recommended, spread };
    }

    /** Универсальный текст по разбросу */
    static explainSpread(
        spread: SpreadLevel | undefined,
        range: number | null | undefined,
        t: TFunction
    ): string {
        return explainSpreadAuto(spread, range, t);
    }

    /**
     * Переменная для отрисовки разброса
     */
    static RangeClass(range: number | null | undefined, t: TFunction): string {
        if (range == null) return "";
        if (range > 10) return "highSpread";
        if (range > 3) return "mediumSpread";
        return "lowSpread";
    }
}
